import { CacheManager } from '../utils/cacheManager';
import safeStorage from '../utils/safeStorage';
import { STORAGE_KEYS } from '../utils/storageKeys';
import { API_ENDPOINTS, BUSINESS_ID, buildQueryString, API_BASE_URL } from '../config/api';

/**
 * Map Mango Business System product to frontend product format
 */
const mapMangoProductToFrontend = (mangoProduct) => {
  // Helper function to construct full image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    // If already a full URL (http/https), return as-is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    // If it's a relative path starting with /uploads, prepend API base URL (without /api/v1)
    if (imagePath.startsWith('/uploads')) {
      const apiBase = API_BASE_URL.replace('/api/v1', '');
      return `${apiBase}${imagePath}`;
    }
    // Otherwise return as-is (might be a data URL or other format)
    return imagePath;
  };

  // Handle images - Mango stores as JSON array
  let images = [];
  if (mangoProduct.images) {
    if (typeof mangoProduct.images === 'string') {
      try {
        images = JSON.parse(mangoProduct.images);
      } catch (e) {
        images = [];
      }
    } else if (Array.isArray(mangoProduct.images)) {
      images = mangoProduct.images;
    }
  }

  // Convert image paths to full URLs
  images = images.map(img => getImageUrl(img)).filter(img => img !== null);

  // Get primary image or first image (with full URL)
  const primaryImage = getImageUrl(mangoProduct.primaryImage) || (images.length > 0 ? images[0] : null);

  // Map gallery images format (for compatibility with existing frontend)
  const galleryImages = images.map((url, index) => ({
    url: url,
    index: index,
    isPrimary: index === 0 || url === primaryImage
  }));

  // Map variants if they exist
  const variants = mangoProduct.variants && Array.isArray(mangoProduct.variants)
    ? mangoProduct.variants.map(v => ({
        id: v.id,
        name: v.name,
        price: v.price || mangoProduct.price,
        compareAtPrice: v.compareAtPrice || mangoProduct.compareAtPrice,
        inventoryQty: v.inventoryQty || 0,
        isActive: v.isActive !== false,
        options: {
          option1: v.option1Name && v.option1Value ? { name: v.option1Name, value: v.option1Value } : null,
          option2: v.option2Name && v.option2Value ? { name: v.option2Name, value: v.option2Value } : null,
          option3: v.option3Name && v.option3Value ? { name: v.option3Name, value: v.option3Value } : null,
        },
        image: v.image || primaryImage,
      }))
    : [];

  // Build frontend product object
  const frontendProduct = {
    // Basic Info
    id: mangoProduct.id,
    name: mangoProduct.name,
    description: mangoProduct.description || '',
    shortDescription: mangoProduct.shortDescription || mangoProduct.description || '',
    
    // Pricing
    price: parseFloat(mangoProduct.price) || 0,
    compareAtPrice: mangoProduct.compareAtPrice ? parseFloat(mangoProduct.compareAtPrice) : null,
    
    // Product Type
    productType: mangoProduct.productType || 'physical',
    type: mangoProduct.productType || 'physical',
    category: mangoProduct.category || '',
    
    // Images
    images: images,
    primaryImage: primaryImage,
    customImage: primaryImage,
    galleryImages: galleryImages,
    folder: null, // Not used in Mango system, but kept for compatibility
    
    // Variants
    variants: variants,
    hasVariants: mangoProduct.hasVariants || (variants.length > 0),
    
    // Subscription Info
    subscription: mangoProduct.productType === 'subscription',
    subscriptionPrice: mangoProduct.subscriptionBasePrice ? parseFloat(mangoProduct.subscriptionBasePrice) : null,
    subscriptionInterval: mangoProduct.subscriptionBillingCycle || null,
    
    // Inventory
    trackInventory: mangoProduct.trackInventory !== false,
    inventoryQty: mangoProduct.inventoryQty || 0,
    lowStockThreshold: mangoProduct.lowStockThreshold || 5,
    
    // Service Product Settings (for coaching, consulting, etc.)
    maxClients: mangoProduct.maxClients || null,
    remainingSpots: mangoProduct.remainingSpots !== undefined ? mangoProduct.remainingSpots : null,
    
    // Status
    published: mangoProduct.isActive !== false,
    isActive: mangoProduct.isActive !== false,
    isFeatured: mangoProduct.isFeatured || false,
    
    // SEO
    slug: mangoProduct.slug || null,
    seoTitle: mangoProduct.seoTitle || null,
    seoDescription: mangoProduct.seoDescription || null,
    
    // Tags and Collections
    tags: mangoProduct.tags ? (typeof mangoProduct.tags === 'string' ? JSON.parse(mangoProduct.tags) : mangoProduct.tags) : [],
    collections: mangoProduct.collections ? (typeof mangoProduct.collections === 'string' ? JSON.parse(mangoProduct.collections) : mangoProduct.collections) : [],
    
    // Metadata
    createdAt: mangoProduct.createdAt,
    updatedAt: mangoProduct.updatedAt,
  };

  return frontendProduct;
};

class ProductsService {
  constructor() {
    this.cacheManager = new CacheManager(5 * 60 * 1000); // 5 minutes cache
    
    console.log('🚀 [SERVICE] ProductsService initialized');
    console.log('🌐 [SERVICE] Using Mango Business System API');
    console.log('🌐 [SERVICE] API Base:', API_ENDPOINTS.products.public);
    console.log('🌐 [SERVICE] Business ID:', BUSINESS_ID);
    console.log('⏰ [TIME]', new Date().toISOString());
  }

  async getProducts(options = {}) {
    console.log('🚀 [FUNCTION] getProducts called');
    console.log('⏰ [TIME]', new Date().toISOString());
    console.log('🚀 [START] Fetching products from Mango API...');
    
    // Check cache first (unless forceRefresh is true)
    if (!options.forceRefresh) {
      const cached = this.cacheManager.get();
      if (cached) {
        console.log('💾 [CACHE] Returning cached products:', cached.length);
        return cached;
      }
    } else {
      this.clearCache();
      console.log('🗑️ [CACHE] Cache cleared - forcing fresh fetch');
    }

    try {
      // Build query parameters
      const queryParams = {
        businessId: BUSINESS_ID,
        isActive: 'true',
        limit: options.limit || 100, // Get more products by default
        page: options.page || 1,
      };

      // Add optional filters
      if (options.category) queryParams.category = options.category;
      if (options.productType) queryParams.productType = options.productType;
      if (options.search) queryParams.search = options.search;
      if (options.isFeatured !== undefined) queryParams.isFeatured = options.isFeatured;

      const queryString = buildQueryString(queryParams);
      const url = `${API_ENDPOINTS.products.public}?${queryString}`;
      
      console.log('📡 [API] Fetching products from Mango API:', url);
      console.log('🌐 [FETCH] Starting fetch request...');
      
      const response = await fetch(url);
      console.log('📡 [API] Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📊 [API] Raw data received from Mango API:', {
        productsCount: data.products?.length || 0,
        pagination: data.pagination
      });
      
      // Map Mango products to frontend format
      const mangoProducts = data.products || [];
      const frontendProducts = mangoProducts.map(mapMangoProductToFrontend);
      
      console.log('📊 [API] Products mapped to frontend format:', {
        productsCount: frontendProducts.length,
        products: frontendProducts.map(p => ({ id: p.id, name: p.name, price: p.price }))
      });
      
      // Cache the mapped products
      this.cacheManager.set(frontendProducts);
      // Also save to localStorage as fallback
      this.saveProductsToLocalStorage(frontendProducts);
      console.log('💾 [CACHE] Products cached successfully');
      console.log('✅ [SUCCESS] Products loaded from Mango API:', frontendProducts.length);
      return frontendProducts;
    } catch (error) {
      console.error('❌ [ERROR] Error fetching products from Mango API:', error);
      console.error('❌ [ERROR] Error details:', {
        message: error.message,
        stack: error.stack,
        url: API_ENDPOINTS.products.public
      });
      
      // Fallback to localStorage if API fetch fails
      const fallbackProducts = this.getProductsFromLocalStorage();
      if (fallbackProducts.length > 0) {
        console.warn('⚠️ API fetch failed, using localStorage fallback. This may be stale data.');
        console.log('📦 [PRODUCTS-SERVICE] Fallback products from localStorage:', fallbackProducts.length);
        // Also update the cache with fallback data
        this.cacheManager.set(fallbackProducts);
        return fallbackProducts;
      }
      
      // Return empty array if no fallback available
      console.error('❌ No products available from API or localStorage');
      return [];
    }
  }

  async getProduct(productId) {
    console.log('🚀 [FUNCTION] getProduct called with ID:', productId);
    console.log('⏰ [TIME]', new Date().toISOString());
    console.log('🚀 [START] Fetching individual product from Mango API...');
    
    try {
      const url = API_ENDPOINTS.products.publicById(productId);
      console.log('📡 [API] Fetching product from Mango API:', url);
      console.log('🌐 [FETCH] Starting fetch request for product:', productId);
      
      const response = await fetch(url);
      console.log('📡 [API] Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url,
        productId
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          console.log('⚠️ [WARN] Product not found:', productId);
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const mangoProduct = data.product || data; // Handle both formats
      
      console.log('📊 [API] Product data received from Mango API:', {
        productId: mangoProduct?.id,
        productName: mangoProduct?.name,
        hasImages: !!mangoProduct?.images,
        hasVariants: !!mangoProduct?.variants,
        isActive: mangoProduct?.isActive
      });
      
      // Map to frontend format
      const frontendProduct = mapMangoProductToFrontend(mangoProduct);
      
      console.log('✅ [SUCCESS] Product loaded and mapped successfully:', productId);
      return frontendProduct;
    } catch (error) {
      console.error('❌ [ERROR] Error fetching product from Mango API:', error);
      console.error('❌ [ERROR] Error details:', {
        productId,
        message: error.message,
        stack: error.stack,
        url: API_ENDPOINTS.products.publicById(productId)
      });
      return null;
    }
  }

  clearCache() {
    console.log('🚀 [FUNCTION] clearCache called');
    console.log('⏰ [TIME]', new Date().toISOString());
    console.log('🗑️ [CACHE] Clearing products cache...');
    
    this.cacheManager.clear();
    
    console.log('✅ [SUCCESS] Products cache cleared successfully');
  }

  /**
   * Get products from localStorage (fallback)
   */
  getProductsFromLocalStorage() {
    const products = safeStorage.getJSON(STORAGE_KEYS.PRODUCTS || 'rainbow_sails_products', []);
    console.log('💾 [PRODUCTS-SERVICE] Retrieved from localStorage:', {
      count: products.length,
      sample: products.length > 0 ? {
        id: products[0].id,
        name: products[0].name,
        price: products[0].price
      } : null
    });
    return products;
  }

  /**
   * Save products to localStorage (for offline access)
   */
  saveProductsToLocalStorage(products) {
    if (safeStorage.setJSON(STORAGE_KEYS.PRODUCTS || 'rainbow_sails_products', products)) {
      console.log('💾 Products saved to localStorage:', {
        totalItems: products.length
      });
    }
  }
}

const productsServiceInstance = new ProductsService();
export default productsServiceInstance;